// Enhanced DomainActivityEditor - Modern, compact tabs and navigation
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import './DomainActivityEditor.css';
import PartBNavigator from './PartBNavigator';
import PartBPage1 from './PartBPage1';
import PartBPage2 from './PartBPage2';
import PartBPage3 from './PartBPage3';
import PartBPage4 from './PartBPage4';
import PartBPage5 from './PartBPage5';
import PartBPage6 from './PartBPage6';
import PartBPage7 from './PartBPage7';
import PartBPage8 from './PartBPage8';
import PartCPage1 from './PartCPage1';
import PartCPage2 from './PartCPage2';
import PartCPage3 from './PartCPage3';
import PartCPage4 from './PartCPage4';
import PartCPage5 from './PartCPage5';
import PartCPage6 from './PartCPage6';
import PartCPage7 from './PartCPage7';
import PartCPage8 from './PartCPage8';
import PartCPage9 from './PartCPage9';
import PartCPage10 from './PartCPage10';
import PartDPage1 from './PartDPage1';
import PartDPage2 from './PartDPage2';
import PartDPage3 from './PartDPage3';
import PartDPage4 from './PartDPage4';
import PartDPage5 from './PartDPage5';
import PartE from './PartE';
import PartFPage1 from './PartFPage1';
import PartFPage2 from './PartFPage2';
import CompetencyProfilePage1 from './CompetencyProfilePage1';
import CompetencyProfilePage2 from './CompetencyProfilePage2';
import Layout from "../../../../Layout";
import { API_CONFIG } from '../../../../config/api';

// Context for activity data
const ActivityContext = createContext();

export const useActivityContext = () => {
    const context = useContext(ActivityContext);
    if (!context) {
        throw new Error('useActivityContext must be used within ActivityContext.Provider');
    }
    return context;
};

const DomainActivityEditor = () => {
    const { activityId } = useParams();
    const navigate = useNavigate();

    const [activity, setActivity] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPart, setCurrentPart] = useState('B');
    const [currentPage, setCurrentPage] = useState(1);
    const [userRole, setUserRole] = useState('student');
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    useEffect(() => {
        fetchActivity();
        determineUserRole();
    }, [activityId]);

    const determineUserRole = () => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        setUserRole(user.role || 'student');
    };

    const fetchActivity = async () => {
        try {
            setLoading(true);
            setError(null);

            const token = localStorage.getItem('accessToken');
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const role = user.role || 'student';

            const endpoint = role === 'teacher'
                ? `/secondary/teacher/domain-activities/${activityId}`
                : `/secondary/user/domain-activities/${activityId}`;

            const apiUrl = API_CONFIG.BASE_URL;
            const response = await axios.get(
                `${apiUrl}${endpoint}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            setActivity(response.data);
        } catch (err) {
            console.error('Error fetching activity:', err);
            setError(err.response?.data?.detail || 'Failed to load activity');
        } finally {
            setLoading(false);
        }
    };

    const handlePartChange = (part) => {
        if (hasUnsavedChanges) {
            const confirmLeave = window.confirm(
                'You have unsaved changes. Are you sure you want to switch parts?'
            );
            if (!confirmLeave) return;
        }
        setCurrentPart(part);
        setCurrentPage(1);
        setHasUnsavedChanges(false);
    };

    const handlePageChange = (pageNumber) => {
        if (hasUnsavedChanges) {
            const confirmLeave = window.confirm(
                'You have unsaved changes. Are you sure you want to leave this page?'
            );
            if (!confirmLeave) return;
        }
        setCurrentPage(pageNumber);
        setHasUnsavedChanges(false);
    };

    const handleBack = () => {
        if (hasUnsavedChanges) {
            const confirmLeave = window.confirm(
                'You have unsaved changes. Are you sure you want to leave?'
            );
            if (!confirmLeave) return;
        }
        navigate('/secondary-domain-activities');
    };

    const refreshActivity = () => {
        fetchActivity();
    };

    const renderCurrentPage = () => {
        if (!activity) return null;

        if (currentPart === 'B') {
            const pages = [PartBPage1, PartBPage2, PartBPage3, PartBPage4, PartBPage5, PartBPage6, PartBPage7, PartBPage8];
            const PageComponent = pages[currentPage - 1] || PartBPage1;
            return <PageComponent />;
        } else if (currentPart === 'C') {
            const pages = [PartCPage1, PartCPage2, PartCPage3, PartCPage4, PartCPage5, PartCPage6, PartCPage7, PartCPage8, PartCPage9, PartCPage10];
            const PageComponent = pages[currentPage - 1] || PartCPage1;
            return <PageComponent />;
        } else if (currentPart === 'D') {
            const pages = [PartDPage1, (props) => <PartDPage2 activity={activity} {...props} />, PartDPage3, PartDPage4, PartDPage5];
            const PageComponent = pages[currentPage - 1] || PartDPage1;
            return <PageComponent />;
        } else if (currentPart === 'E') {
            return <PartE />;
        } else if (currentPart === 'F') {
            const pages = [PartFPage1, PartFPage2];
            const PageComponent = pages[currentPage - 1] || PartFPage1;
            return <PageComponent />;
        } else if (currentPart === 'CP') {
            const pages = [CompetencyProfilePage1, CompetencyProfilePage2];
            const PageComponent = pages[currentPage - 1] || CompetencyProfilePage1;
            return <PageComponent />;
        }
    };

    const partConfig = {
        'B': { label: 'Project', icon: '🎯', color: 'from-orange-400 to-red-400', pages: 8 },
        'C': { label: 'Inquiry', icon: '🔍', color: 'from-blue-400 to-indigo-400', pages: 10 },
        'D': { label: 'Observation', icon: '👁️', color: 'from-purple-400 to-pink-400', pages: 5 },
        'E': { label: 'Online', icon: '💻', color: 'from-green-400 to-teal-400', pages: 1 },
        'F': { label: 'Hours', icon: '⏱️', color: 'from-yellow-400 to-orange-400', pages: 2 },
        'CP': { label: 'Profile', icon: '📊', color: 'from-cyan-400 to-blue-400', pages: 2 }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
                    <div className="text-center">
                        <div className="relative inline-block">
                            <div className="w-20 h-20 border-4 border-gray-200 border-t-[#D67C2C] rounded-full animate-spin"></div>
                        </div>
                        <p className="mt-6 text-gray-600 font-medium text-lg">Loading activity...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    if (error || !activity) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-red-50">
                    <div className="text-center max-w-md p-8">
                        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <p className="text-red-600 font-semibold text-xl mb-6">{error || 'Activity not found'}</p>
                        <button
                            onClick={handleBack}
                            className="px-6 py-3 bg-gradient-to-r from-[#D67C2C] to-[#F17C45] text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                        >
                            ← Back to Activities
                        </button>
                    </div>
                </div>
            </Layout>
        );
    }

    const contextValue = {
        activity,
        userRole,
        currentPart,
        currentPage,
        hasUnsavedChanges,
        setHasUnsavedChanges,
        refreshActivity
    };

    return (
        <Layout>
            <ActivityContext.Provider value={contextValue}>
                <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-orange-50">
                    {/* Compact Header */}
                    <div className="bg-white shadow-lg border-b border-gray-200">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                            <div className="flex items-center justify-between">
                                <button
                                    onClick={handleBack}
                                    className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-[#D67C2C] font-medium rounded-lg hover:bg-gray-100 transition-all duration-300"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                    Back
                                </button>
                                <div className="flex-1 text-center px-4">
                                    <h1 className="text-2xl font-bold text-[#1D3469] truncate">{activity.title}</h1>
                                    {activity.due_date && (
                                        <p className="text-sm text-gray-600 mt-1">
                                            📅 Due: {new Date(activity.due_date).toLocaleDateString()}
                                        </p>
                                    )}
                                </div>
                                <div className="w-24"></div>
                            </div>
                        </div>
                    </div>

                    {/* Compact Part Tabs */}
                    <div className="bg-white shadow-md border-b border-gray-200">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                            <div className="flex gap-2 overflow-x-auto py-3 scrollbar-hide">
                                {Object.entries(partConfig).map(([key, config]) => (
                                    <button
                                        key={key}
                                        onClick={() => handlePartChange(key)}
                                        className={`flex-shrink-0 px-4 py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 flex items-center gap-2 ${currentPart === key
                                                ? `bg-gradient-to-r ${config.color} text-white shadow-lg transform scale-105`
                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:scale-105'
                                            }`}
                                    >
                                        <span className="text-base">{config.icon}</span>
                                        <span className="whitespace-nowrap">{config.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Page Navigator - Compact */}
                    {currentPart !== 'E' && (
                        <div className="bg-white shadow-sm border-b border-gray-200">
                            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
                                <div className="flex items-center justify-between">
                                    <p className="text-sm font-medium text-gray-600">
                                        Page {currentPage} of {partConfig[currentPart].pages}
                                    </p>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-300 ${currentPage === 1
                                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                    : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:shadow-lg hover:-translate-y-0.5'
                                                }`}
                                        >
                                            ← Previous
                                        </button>
                                        <button
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === partConfig[currentPart].pages}
                                            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-300 ${currentPage === partConfig[currentPart].pages
                                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                    : 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:shadow-lg hover:-translate-y-0.5'
                                                }`}
                                        >
                                            Next →
                                        </button>
                                    </div>
                                    <div className="flex gap-1">
                                        {Array.from({ length: partConfig[currentPart].pages }, (_, i) => i + 1).map(page => (
                                            <button
                                                key={page}
                                                onClick={() => handlePageChange(page)}
                                                className={`w-8 h-8 rounded-lg font-medium text-xs transition-all duration-300 ${currentPage === page
                                                        ? `bg-gradient-to-r ${partConfig[currentPart].color} text-white shadow-md`
                                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                    }`}
                                            >
                                                {page}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Page Content */}
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                        {renderCurrentPage()}
                    </div>
                </div>
            </ActivityContext.Provider>
        </Layout>
    );
};

export default DomainActivityEditor;
